/**
 * useFilters Hook
 * Centralized filter state management for dashboard
 *
 * Responsibilities:
 * - Read URL params on mount
 * - Manage filter state (filters, page, pageSize, sorting)
 * - Sync state to URL (one-way)
 * - Provide all filter change handlers
 */

import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { serializeFiltersToUrl, deserializeFiltersFromUrl } from 'src/utils/filterUrlSerializer';
import { COLUMN_FILTER_MAP } from 'src/presentations/components/molecules/table/columnHeader';
import type { FiltersDto, InvoiceSearchRequestDto } from 'src/generated/services/TFinancialApi';

interface UseFiltersOptions {
  defaultCurrency: string;
}

interface FilterState {
  currencyReference: string;
  dateReference: InvoiceSearchRequestDto['dateReference'];
  invoiceStatusCategory: InvoiceSearchRequestDto['invoiceStatusCategory'];
  invoiceStatusSet: InvoiceSearchRequestDto['invoiceStatusSet'];
  filters: FiltersDto;
}

export const useFilters = ({ defaultCurrency }: UseFiltersOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL (lazy - only runs once on mount)
  const [filters, setFilters] = useState<FilterState>(() => initializeFiltersFromUrl(searchParams, defaultCurrency));

  const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '1', 10));

  const [pageSize, setPageSize] = useState(() => parseInt(searchParams.get('pageSize') || '25', 10));

  const [orderBy, setOrderBy] = useState(() => searchParams.get('orderBy') || 'INVOICE_DATE');

  const [orderMethod, setOrderMethod] = useState<'asc' | 'desc'>(() => (searchParams.get('orderMethod') as 'asc' | 'desc') || 'asc');

  // Helper to update URL params (called from handlers only)
  const syncUrlParams = useCallback(
    (updates: { filters?: FilterState; page?: number; pageSize?: number; orderBy?: string; orderMethod?: 'asc' | 'desc' }) => {
      updateUrlParams({
        filters: updates.filters ?? filters,
        page: updates.page ?? page,
        pageSize: updates.pageSize ?? pageSize,
        orderBy: updates.orderBy ?? orderBy,
        orderMethod: updates.orderMethod ?? orderMethod,
        searchParams,
        setSearchParams,
      });
    },
    [filters, page, pageSize, orderBy, orderMethod, searchParams, setSearchParams]
  );

  // State to store filter history per status category
  // Initialize from localStorage to persist across reloads
  const [filterHistory, setFilterHistory] = useState<Record<string, FiltersDto>>(() => {
    try {
      const stored = localStorage.getItem('dashboard_filter_history');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load filter history from localStorage:', error);
    }
    return {};
  });

  // Persist filter history to localStorage whenever it changes
  const updateFilterHistory = useCallback((newHistory: Record<string, FiltersDto>) => {
    setFilterHistory(newHistory);
    try {
      localStorage.setItem('dashboard_filter_history', JSON.stringify(newHistory));
    } catch (error) {
      console.error('Failed to save filter history to localStorage:', error);
    }
  }, []);

  // Handler: Change top-level filters (currency, date reference, etc.)
  const handleFilterChange = useCallback(
    (newFilters: Partial<FilterState>) => {
      const updatedFilters = {
        ...filters,
        ...newFilters,
      };
      setFilters(updatedFilters);
      setPage(1);

      // Update URL immediately after state change
      syncUrlParams({ filters: updatedFilters, page: 1 });
    },
    [filters, syncUrlParams]
  );

  // Handler: Change table column filters
  const handleTableFilter = useCallback(
    (columnId: string, selectedValues: string[]) => {
      const currentFilters = filters;
      const nested = currentFilters.filters || {};

      // Use shared filter mapping
      const mapping = COLUMN_FILTER_MAP[columnId];
      const apiKey = mapping?.apiKey || columnId;
      const filterGroup = mapping?.filterGroup || 'shipmentFilters';

      // Create new nested structure
      const newNested = { ...nested };

      if (filterGroup === 'filters') {
        // Put directly in filters for status and invoiceNumber
        newNested[apiKey] = selectedValues;
      } else {
        // Put in appropriate filter group
        newNested[filterGroup] = {
          ...newNested[filterGroup],
          [apiKey]: selectedValues,
        };
      }

      const updatedFilters = { ...currentFilters, filters: newNested };
      setFilters(updatedFilters);
      setPage(1);

      // Save current tab's filters to history
      const currentStatus = currentFilters.invoiceStatusCategory || 'ACTION_NEEDED';
      const updatedHistory = {
        ...filterHistory,
        [currentStatus]: newNested,
      };
      updateFilterHistory(updatedHistory);

      // Update URL after user action
      syncUrlParams({ filters: updatedFilters, page: 1 });
    },
    [filters, syncUrlParams, filterHistory, updateFilterHistory]
  );

  // Handler: Change status category (tab)
  const handleStatusChange = useCallback(
    (status: InvoiceSearchRequestDto['invoiceStatusCategory']) => {
      // 1. Save current filters to history
      const currentStatus = filters.invoiceStatusCategory || 'ACTION_NEEDED';
      const currentFilters = filters.filters || {};

      const updatedHistory = {
        ...filterHistory,
        [currentStatus]: currentFilters,
      };
      updateFilterHistory(updatedHistory);

      // 2. Load filters from history for the new status
      // Always use the current URL provider/modality as the base context,
      // only restore column-level filters from history.
      const providerCode = searchParams.get('provider');
      const modality = searchParams.get('modality');
      const baseShipmentFilters = {
        serviceProviderCode: providerCode && providerCode !== '' ? [providerCode] : undefined,
        modality: modality && modality !== '' ? [modality] : undefined,
      };

      const historyFilters = updatedHistory[status];
      let nextFilters: FiltersDto;

      if (historyFilters) {
        // Restore history but always override provider/modality with current context
        nextFilters = {
          ...historyFilters,
          shipmentFilters: {
            ...historyFilters.shipmentFilters,
            ...baseShipmentFilters,
          },
        };
      } else {
        nextFilters = {
          dateFilters: {},
          tradeLaneFilters: {},
          shipmentFilters: baseShipmentFilters,
        };
      }

      const updatedFilters: FilterState = {
        ...filters,
        invoiceStatusCategory: status,
        filters: nextFilters,
      };

      setFilters(updatedFilters);
      setPage(1);

      // Update URL after user action
      syncUrlParams({ filters: updatedFilters, page: 1 });
    },
    [filters, filterHistory, searchParams, syncUrlParams, updateFilterHistory]
  );

  // Handler: Change page
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);

      // Update URL after user action
      syncUrlParams({ page: newPage });
    },
    [syncUrlParams]
  );

  // Handler: Change page size
  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      setPageSize(newSize);
      setPage(1);

      // Update URL after user action
      syncUrlParams({ pageSize: newSize, page: 1 });
    },
    [syncUrlParams]
  );

  // Handler: Change sorting
  const handleSort = useCallback(
    (method: 'asc' | 'desc', fieldName: string) => {
      const fieldMap: Record<string, string> = {
        invoiceNumber: 'INVOICE_NUMBER',
        shipmentNumber: 'SHIPMENTS',
        tradeLane: 'TRADE_LANES',
        service: 'SERVICES',
        PICKUP_DATE: 'PICKUP_DATE',
        DELIVERY_DATE: 'DELIVERY_DATE',
        INVOICE_DATE: 'INVOICE_DATE',
        INVOICE_DUE_DATE: 'INVOICE_DUE_DATE',
        SUBMISSION_DATE: 'SUBMISSION_DATE',
        shipments: 'SHIPMENTS',
        tradeLanes: 'TRADE_LANES',
        services: 'SERVICES',
        totalAmount: 'TOTAL_AMOUNT',
        status: 'INVOICE_STATUS',
        deviationAmount: 'DEVIATION_AMOUNT',
        incoterm: 'INCOTERM',
        billedEntity: 'BILLED_ENTITY',
        shipper: 'SHIPPER',
        consignee: 'CONSIGNEE',
        aggregateShipmentType: 'SHIPMENT_TYPE',
      };

      const fieldId = fieldMap[fieldName] || fieldName;
      setOrderMethod(method);
      setOrderBy(fieldId);

      // Update URL after user action
      syncUrlParams({ orderMethod: method, orderBy: fieldId });
    },
    [syncUrlParams]
  );

  // Handler: Reset filters
  const handleReset = useCallback(() => {
    // Only preserve serviceProviderCode and modality (base context filters)
    const baseShipmentFilters = filters.filters?.shipmentFilters || {};
    const { serviceProviderCode, modality } = baseShipmentFilters;

    const resetFilters = {
      dateFilters: {},
      tradeLaneFilters: {},
      shipmentFilters: {
        serviceProviderCode,
        modality,
      },
    };

    const updatedFilters = {
      ...filters,
      filters: resetFilters,
    };

    setFilters(updatedFilters);
    setPage(1);

    // Save reset filters to history for current tab
    const currentStatus = filters.invoiceStatusCategory || 'ACTION_NEEDED';
    const updatedHistory = {
      ...filterHistory,
      [currentStatus]: resetFilters,
    };
    updateFilterHistory(updatedHistory);

    // Update URL after user action
    syncUrlParams({ filters: updatedFilters, page: 1 });
  }, [filters, syncUrlParams, filterHistory, updateFilterHistory]);

  // Helper: Calculate counter display info for a column
  const getColumnCounterInfo = (columnId: string, allOptionsCount: number) => {
    const currentFilters = filters;
    const nested = currentFilters.filters || {};

    // Use shared filter mapping
    const mapping = COLUMN_FILTER_MAP[columnId];
    if (!mapping) {
      return { showCounter: false, counterValue: 0 };
    }

    let selectedFilters: any[] | undefined;

    if (mapping.filterGroup === 'filters') {
      selectedFilters = nested[mapping.apiKey];
    } else {
      const filterGroup = nested[mapping.filterGroup];
      selectedFilters = filterGroup?.[mapping.apiKey];
    }

    const selectedCount = selectedFilters?.length || 0;
    const totalCount = allOptionsCount;

    // Show counter only for partial selection
    const showCounter = selectedCount > 0 && selectedCount < totalCount;

    return {
      showCounter,
      counterValue: selectedCount,
    };
  };

  return {
    // State
    filters,
    page,
    pageSize,
    orderBy,
    orderMethod,

    // Handlers
    handleFilterChange,
    handleTableFilter,
    handleStatusChange,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleReset,

    // Helper
    getColumnCounterInfo,
    // Exposed for metadata fetching
    filterHistory,
  };
};

/**
 * Initialize filters from URL parameters
 */
function initializeFiltersFromUrl(searchParams: URLSearchParams, defaultCurrency: string): FilterState {
  const providerCode = searchParams.get('provider');
  const modality = searchParams.get('modality');

  // Base filters
  const filters: FilterState = {
    currencyReference: searchParams.get('currencyReference') || defaultCurrency || '',
    dateReference: (searchParams.get('dateReference') as InvoiceSearchRequestDto['dateReference']) || 'PICKUP_DATE',
    invoiceStatusCategory: (searchParams.get('invoiceStatusCategory') as InvoiceSearchRequestDto['invoiceStatusCategory']) || 'ACTION_NEEDED',
    invoiceStatusSet: (searchParams.get('invoiceStatusSet')?.split(',') as InvoiceSearchRequestDto['invoiceStatusSet']) || [],
    filters: {
      dateFilters: {},
      tradeLaneFilters: {},
      shipmentFilters: {
        serviceProviderCode: providerCode && providerCode !== '' ? [providerCode] : undefined,
        modality: modality && modality !== '' ? [modality] : undefined,
      },
    },
  };

  // Deserialize table filters if present
  const tableFiltersParam = searchParams.get('tableFilters');
  if (tableFiltersParam) {
    const deserializedFilters = deserializeFiltersFromUrl(tableFiltersParam);
    if (deserializedFilters) {
      filters.filters = {
        ...filters.filters,
        ...deserializedFilters,
      };
    }
  }

  return filters;
}

/**
 * Update URL parameters based on current state
 */
function updateUrlParams(params: {
  filters: FilterState;
  page: number;
  pageSize: number;
  orderBy: string;
  orderMethod: string;
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams, options?: any) => void;
}) {
  const { filters, page, pageSize, orderBy, orderMethod, searchParams, setSearchParams } = params;

  const newSearchParams = new URLSearchParams();

  // Preserve provider and modality from current URL
  const currentProvider = searchParams.get('provider');
  const currentModality = searchParams.get('modality');
  const currentInvoiceId = searchParams.get('invoiceId');

  if (currentProvider) {
    newSearchParams.set('provider', currentProvider);
  }
  if (currentModality) {
    newSearchParams.set('modality', currentModality);
  }
  if (currentInvoiceId) {
    newSearchParams.set('invoiceId', currentInvoiceId);
  }

  // Add pagination
  if (page && page > 1) {
    newSearchParams.set('page', String(page));
  }
  if (pageSize && pageSize !== 25) {
    newSearchParams.set('pageSize', String(pageSize));
  }

  // Add sorting (only if not default)
  if (orderBy && orderBy !== 'INVOICE_DATE') {
    newSearchParams.set('orderBy', orderBy);
  }
  if (orderMethod && orderMethod !== 'asc') {
    newSearchParams.set('orderMethod', orderMethod);
  }

  // Serialize nested table filters
  if (filters.filters) {
    const serializedTableFilters = serializeFiltersToUrl(filters.filters);
    if (serializedTableFilters) {
      newSearchParams.set('tableFilters', serializedTableFilters);
    }
  }

  // Add top-level filter parameters
  if (filters.currencyReference) {
    newSearchParams.set('currencyReference', filters.currencyReference);
  }
  if (filters.dateReference) {
    newSearchParams.set('dateReference', filters.dateReference);
  }
  if (filters.invoiceStatusCategory) {
    newSearchParams.set('invoiceStatusCategory', filters.invoiceStatusCategory);
  }
  if (filters.invoiceStatusSet && filters.invoiceStatusSet.length > 0) {
    newSearchParams.set('invoiceStatusSet', filters.invoiceStatusSet.join(','));
  }

  setSearchParams(newSearchParams, { replace: true });
}
